import { api } from "./api";

/**
 * The committed plan, with its schedule flattened back to one row per step.
 *
 * The API returns `schedule` grouped — one object per work order with its
 * steps nested — because that is the shape a batch-and-its-steps view wants.
 * Almost everything here wants the opposite: the Gantt, the KPI counts and
 * the commit POST all work in flat step rows, and the POST in particular
 * sends this array straight back to the server, which validates it as
 * ScheduleEntry objects and would reject grouped ones.
 *
 * So the flattening happens once, here at the boundary, rather than in each
 * of the dozen call sites. `work_orders` carries the grouped form through
 * unchanged for callers that want it.
 */
const flattenSchedule = (plan) => {
  const groups = plan?.schedule;
  if (!Array.isArray(groups) || !groups.length) return plan;
  // Already flat (a step row has `seq`, a group has `steps`) — nothing to do.
  if (!groups[0]?.steps) return plan;
  return {
    ...plan,
    work_orders: groups,
    schedule: groups.flatMap((g) =>
      (g.steps ?? []).flatMap((s) => {
        // The batch-level fields live on the parent; put them back on each
        // row so a flattened entry is identical to what the API used to send.
        const batch = {
          // `batch_no` identifies the batch, so it has to come back down
          // onto every row: a process order's code covers many batches, and
          // a flattened row without it names the order and not the run.
          batch_no: g.batch_no,
          product_id: g.product_id,
          batch_status: g.batch_status,
        };
        if (!Array.isArray(s.allocations) || !s.allocations.length) {
          return [{ ...s, ...batch }];
        }
        // A grouped SPLIT step: the API folds its rows into one step object
        // carrying an `allocations` list plus an envelope start/end (earliest
        // allocation start, latest end). The flat shape — and the commit POST
        // that sends this array straight back — wants one row per allocation,
        // each carrying allocation_no/quantity_kg/equipment_id and its own
        // times, so the envelope fields are dropped in favour of the
        // allocation's. Unsplit steps above pass through byte-identical.
        const { allocations, start: _start, end: _end, ...step } = s;
        return allocations.map((a) => ({ ...step, ...a, ...batch }));
      }),
    ),
  };
};

export const getCommittedPlan = (unit) =>
  api.get("/api/commit", { params: { unit } }).then(flattenSchedule);

/**
 * Publishes the agreed plan. `sign` carries the e-signature credentials the
 * UI collects; the server verifies them and records the signer.
 *
 * `lateChangeReason` feeds the conscious late-change gate: when the commit
 * moves or drops operations committed to start within the unit's
 * late-change window, the server refuses (400 with a `late_changes` list)
 * unless a reason is stated — which then lands in the audit trail and
 * opens a Planning variance case.
 */
export const commitPlan = (
  unit,
  { note, schedule, sign, lateChangeReason, planType, label, period, facts },
) =>
  api.post(
    "/api/commit",
    {
      note,
      schedule,
      sign_username: sign?.username,
      sign_password: sign?.password,
      late_change_reason: lateChangeReason || undefined,
      // The solve's fact fingerprint. Present, the server refuses (409,
      // naming what moved) when the floor changed after the solve; absent
      // (committing without a fresh solve on the board), unchanged behavior.
      facts: facts || undefined,
      // Monthly mode. A monthly plan is a labelled month-horizon document:
      // approved, it becomes the month's named baseline. Omitted entirely
      // for a daily commit so the payload keeps its old shape.
      plan_type: planType || undefined,
      label: label || undefined,
      period: period || undefined,
    },
    { params: { unit } },
  );

/**
 * The monthly plan awaiting a verdict, or null. Carries `may_approve` and
 * `blocked_because` so the page never offers an action the API will refuse,
 * and an `advisory` describing what approving would do to near-term
 * committed work — information for the approver, never a block.
 */
export const getPendingPlan = (unit) =>
  api.get("/api/commit/pending", { params: { unit } });

/** Approve the pending monthly plan: it becomes the plan of record. */
export const approvePlan = (unit, version, comments) =>
  api.post("/api/commit/approve", { version, comments }, { params: { unit } });

/** Return it to the planner. Comments are required — a bare "no" tells them nothing. */
export const returnPlan = (unit, version, comments) =>
  api.post("/api/commit/return", { version, comments }, { params: { unit } });

/**
 * The month's named baseline — what attainment and adherence measure
 * against all month. Null when the month was never planned as a month.
 */
export const getPlanBaseline = (unit, period) =>
  api.get("/api/commit/baseline", { params: { unit, period } });

export const getCommitHistory = (unit, { kpis = false } = {}) =>
  api.get("/api/commit/history", { params: { unit, kpis: kpis || undefined } });

export const compareCommits = (unit, a, b) =>
  api.get("/api/commit/compare", { params: { unit, a, b } });

/**
 * Re-commits an earlier version as a new one — history is never rewritten.
 * The restored plan becomes a new signed version, so `sign` (the caller's own
 * credentials) is required just like on a commit.
 */
export const restoreCommit = (unit, version, sign, lateChangeReason) =>
  api.post(
    "/api/commit/restore",
    {
      version,
      sign_username: sign?.username,
      sign_password: sign?.password,
      // Rolling back is committing — the same late-change gate applies.
      late_change_reason: lateChangeReason || undefined,
    },
    { params: { unit } },
  );

/**
 * Audit entries, newest first. `opts` may be a plain number (the legacy
 * limit) or `{ limit, since, until }` — since/until are ISO dates applied
 * server-side, so an older window returns that window's entries rather
 * than whatever survives the newest-N truncation.
 */
export const getAudit = (unit, opts = 100) => {
  const { limit = 100, since, until } =
    typeof opts === "number" ? { limit: opts } : opts ?? {};
  return api.get("/api/audit", { params: { unit, limit, since, until } });
};

/**
 * The audit trail's shape — volume, actors, action mix, daily activity and
 * a completeness statement. Counted server-side over the whole window, so
 * it describes the trail rather than the page the table happens to hold.
 */
export const getAuditStats = (unit, opts = {}) => {
  const { days = 30, since, until } = opts;
  return api.get("/api/audit/stats", { params: { unit, days, since, until } });
};
